import { Button } from '@auctisync/ui';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useMemo, useState } from 'react';
import { invokeEF } from '../lib/edgeFunctions';
import { friendlyPaymentError } from '../lib/paymentErrors';
import { loadStripeForAccount, missingStripeKeyMessage, stripeConfigured } from '../lib/stripe';

/**
 * Putting a card on file — the whole flow, in one place.
 *
 * EXTRACTED from `/my/payment-methods` (2026-09-03) when registration grew a
 * card step. Two surfaces now ask for a card, and two implementations of a
 * payment path is precisely the thing that must not happen: the SetupIntent
 * idempotency, the per-house Stripe Connect account, and the mandate wording
 * are all things you either get right once or get wrong in one of two places.
 *
 * Cards are stored PER auction house. Each tenant is a separate Stripe Connect
 * account, so a card saved with one house is not available to another — which
 * is why every function here takes a tenant.
 */

export interface AddCardContext {
  tenantId: string;
  tenantName: string;
  clientSecret: string;
  stripeAccountId: string;
}

export type StartAddCardResult =
  | { ok: true; context: AddCardContext }
  | { ok: false; message: string };

/**
 * Mint a SetupIntent for this house.
 *
 * `nonce` is the caller's, held across a retry of the SAME house so two racing
 * clicks dedupe to one SetupIntent server-side. It is passed in rather than
 * generated here because only the caller knows whether this is a retry.
 */
export async function startAddCard(params: {
  tenantId: string;
  tenantName: string;
  nonce: string;
}): Promise<StartAddCardResult> {
  if (!stripeConfigured()) {
    return { ok: false, message: missingStripeKeyMessage() };
  }
  const { data, error } = await invokeEF<{
    client_secret: string;
    stripe_account_id: string;
  }>('create-payment-method-setup-intent', {
    tenant_id: params.tenantId,
    nonce: params.nonce,
  });
  if (error || !data?.client_secret || !data.stripe_account_id) {
    return { ok: false, message: friendlyPaymentError(error) };
  }
  return {
    ok: true,
    context: {
      tenantId: params.tenantId,
      tenantName: params.tenantName,
      clientSecret: data.client_secret,
      stripeAccountId: data.stripe_account_id,
    },
  };
}

export function AddCardModal({
  context,
  onClose,
  onSaved,
}: {
  context: AddCardContext;
  onClose: () => void;
  onSaved: (pmId: string | null) => void;
}) {
  const stripePromise = useMemo(
    () => loadStripeForAccount(context.stripeAccountId),
    [context.stripeAccountId],
  );
  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="Add a card">
      <div style={modalStyle}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginBottom: '0.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
            Add a card · {context.tenantName}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtnStyle}>
            ×
          </button>
        </header>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: context.clientSecret, appearance: { theme: 'stripe' } }}
        >
          <AddCardForm tenantName={context.tenantName} onSaved={onSaved} onCancel={onClose} />
        </Elements>
      </div>
    </div>
  );
}

function AddCardForm({
  tenantName,
  onSaved,
  onCancel,
}: {
  tenantName: string;
  onSaved: (pmId: string | null) => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const res = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (res.error) {
      setErr(res.error.message ?? 'Card setup failed.');
      setBusy(false);
      return;
    }
    setBusy(false);
    // Hand the confirmed payment method up so the parent can default it on
    // a first save (Stripe returns either the pm id or an expanded object).
    const si = res.setupIntent;
    const pm =
      typeof si?.payment_method === 'string' ? si.payment_method : (si?.payment_method?.id ?? null);
    onSaved(pm);
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      style={{ display: 'grid', gap: '0.75rem' }}
    >
      <PaymentElement />
      {/* Card-on-file authorization (Stripe records acceptance on confirm).
          First sentence is the spec-approved mandate wording. */}
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
        I authorize <strong>{tenantName}</strong> to charge this card for invoices on lots I win at
        their auctions, and for any refundable bidding deposit their auctions require. You can
        remove the card anytime.
      </p>
      {err && (
        <div role="alert" style={errorStyle}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !stripe}>
          {busy ? 'Saving…' : 'Save card'}
        </Button>
      </div>
    </form>
  );
}

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.875rem',
};

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  zIndex: 50,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1.25rem 1.5rem',
  maxWidth: '28rem',
  width: '100%',
  border: '1px solid var(--brand-hairline, var(--border))',
  boxShadow: 'var(--elev-3)',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: '1.25rem',
  cursor: 'pointer',
  color: 'var(--text-muted)',
};
